import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Download, History, Edit } from 'lucide-react';
import { BudgetSummaryCards } from './budget-summary-cards';
import { AllBudgetsView } from './all-budgets-view';
import { CostBudgetView } from './cost-budget-view';
import { SalesBudgetView } from './sales-budget-view';
import { ResourceBudgetView } from './resource-budget-view';
import { BudgetAlertsSection } from './budget-alerts-section';
import { EditBudgetModal } from './edit-budget-modal';
import { BudgetHistoryModal } from './budget-history-modal';
import { BudgetExportModal } from './budget-export-modal';
import { useBudgetData } from '@/hooks/use-budget-data';
import type { ProjectType, BudgetData } from '@/types';

interface BudgetTabContainerProps {
  projectId: string;
  projectType: ProjectType;
  canEdit?: boolean;
  onEdit?: () => void;
  onViewHistory?: () => void;
  onExport?: () => void;
}

export function BudgetTabContainer({
  projectId,
  projectType,
  canEdit = false,
  onEdit,
  onViewHistory,
  onExport,
}: BudgetTabContainerProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const { data: budgetData, loading, error } = useBudgetData(projectId);

  const handleSaveBudget = async (
    updatedBudget: Partial<BudgetData>,
    revisionReason: string,
    saveAsBaseline: boolean
  ) => {
    console.log('Saving budget:', { updatedBudget, revisionReason, saveAsBaseline });
    // TODO: Implement actual save logic when backend is ready
    // For now, just close the modal
    alert(`Budget saved successfully!\n\nReason: ${revisionReason}\nBaseline: ${saveAsBaseline}`);
  };

  const handleRestoreRevision = async (revisionId: string) => {
    console.log('Restoring revision:', revisionId);
    // TODO: Implement actual restore logic when backend is ready
    alert(`Revision ${revisionId} restored successfully!\n\nA new revision has been created based on the selected version.`);
    setHistoryModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading budget data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <div className="text-destructive mb-2">Error loading budget data</div>
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="text-xs text-muted-foreground">
            <p>Project ID: {projectId}</p>
            <p>Project Type: {projectType}</p>
          </div>
          {canEdit && (
            <Button onClick={onEdit} variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Create Budget
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!budgetData) {
    console.error('[BudgetTabContainer] budgetData is null or undefined');
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">No budget data available</p>
          {canEdit && (
            <Button onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Create Budget
            </Button>
          )}
        </div>
      </div>
    );
  }

  console.log('[BudgetTabContainer] budgetData:', budgetData);
  console.log('[BudgetTabContainer] budgetData keys:', Object.keys(budgetData));
  console.log('[BudgetTabContainer] Has allBudgets:', budgetData.allBudgets !== undefined);
  console.log('[BudgetTabContainer] allBudgets value:', budgetData.allBudgets);

  const { summary, costBudget, salesBudget, resourceBudget, allBudgets, alerts } = budgetData;
  
  console.log('[BudgetTabContainer] Destructured allBudgets:', allBudgets);

  return (
    <div className="space-y-4">
      {/* Summary Cards - Always Visible */}
      <BudgetSummaryCards summary={summary} />

      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Budget Details</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setHistoryModalOpen(true)}>
            <History className="mr-2 h-4 w-4" />
            History
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExportModalOpen(true)}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          {canEdit && (
            <Button size="sm" onClick={() => setEditModalOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Budget
            </Button>
          )}
        </div>
      </div>

      {/* Budget Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all" className="gap-2">
            All Budgets
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Coming Soon
            </span>
          </TabsTrigger>
          <TabsTrigger value="cost" className="gap-2">
            Cost Budget
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Coming Soon
            </span>
          </TabsTrigger>
          <TabsTrigger value="sales" className="gap-2">
            Sales Budget
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Coming Soon
            </span>
          </TabsTrigger>
          <TabsTrigger value="resource">Resource Budget</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <AllBudgetsView data={allBudgets} />
        </TabsContent>

        <TabsContent value="cost" className="space-y-4">
          <CostBudgetView data={costBudget} />
        </TabsContent>

        <TabsContent value="sales" className="space-y-4">
          <SalesBudgetView data={salesBudget} projectType={projectType} />
        </TabsContent>

        <TabsContent value="resource" className="space-y-4">
          <ResourceBudgetView data={resourceBudget} />
        </TabsContent>
      </Tabs>

      {/* Budget Alerts - Always Visible at Bottom */}
      {alerts && alerts.length > 0 && (
        <BudgetAlertsSection alerts={alerts} />
      )}

      {/* Edit Budget Modal */}
      <EditBudgetModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        budgetData={budgetData}
        projectType={projectType}
        onSave={handleSaveBudget}
      />

      {/* Budget History Modal */}
      <BudgetHistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        revisions={budgetData.revisions || []}
        projectName={budgetData.projectName || `Project ${projectId}`}
        onRestore={handleRestoreRevision}
      />

      {/* Budget Export Modal */}
      <BudgetExportModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        budgetData={budgetData}
      />
    </div>
  );
}
